import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, Share2, Link as LinkIcon, MessageSquare, Users, ExternalLink, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OutreachHub — the "distribution" section of the MLO dashboard.
 * Renders the tile catalog from /api/mlo/outreach/tiles plus a small stats strip
 * from /api/mlo/outreach/stats.
 *
 * Each tile lets the MLO:
 *  - Copy just the link
 *  - Pick a tone (Casual/Formal/Follow-up) and copy a pre-written message
 *  - Open the target URL in a new tab to preview it
 */
export default function OutreachHub({ authHeader }) {
  const [tiles, setTiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [tilesRes, statsRes] = await Promise.all([
          axios.get(`${API}/mlo/outreach/tiles`, { headers: authHeader() }),
          axios.get(`${API}/mlo/outreach/stats`, { headers: authHeader() }),
        ]);
        setTiles(tilesRes.data.tiles || []);
        setStats(statsRes.data);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, [authHeader]);

  if (loading) {
    return (
      <div className="bg-white border border-linen-border rounded-3xl shadow-soft p-8 flex items-center gap-3 text-ink-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading outreach kit…
      </div>
    );
  }

  return (
    <section className="space-y-5" data-testid="mlo-outreach-hub">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-teal">
            <Share2 size={13} /> Outreach
          </div>
          <h2 className="mt-1 font-heading font-medium text-2xl text-navy">Share your links</h2>
          <p className="mt-1 text-sm text-ink-500">
            Copy a link or a ready-to-send message. Applications through these links are attributed to you automatically.
          </p>
        </div>
        {stats && <StatsStrip stats={stats} />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tiles.map((t) => (
          <OutreachTile key={t.id} tile={t} />
        ))}
      </div>
    </section>
  );
}

function StatsStrip({ stats }) {
  return (
    <div
      className="flex items-stretch gap-3 text-[11px] uppercase tracking-widest text-ink-500"
      data-testid="mlo-outreach-stats"
    >
      <StatChip
        label="Rate-check starts"
        n={stats.clicks_this_month}
        sub={`all-time ${stats.clicks_all_time}`}
      />
      <StatChip
        label="Partner apps this month"
        n={stats.partner_apps_this_month}
        sub={`all-time ${stats.partner_apps_all_time}`}
        highlight
      />
    </div>
  );
}

function StatChip({ label, n, sub, highlight }) {
  return (
    <div
      className={`rounded-xl px-3 py-2 border ${
        highlight ? "bg-lime/15 border-lime/40" : "bg-linen/70 border-linen-border"
      }`}
    >
      <div className="text-navy font-heading font-medium text-xl leading-none tabular-nums">{n}</div>
      <div className="mt-1 text-[10px] normal-case tracking-normal text-ink-500">{label}</div>
      {sub && <div className="text-[10px] normal-case tracking-normal text-ink-400">{sub}</div>}
    </div>
  );
}

function OutreachTile({ tile }) {
  const [tone, setTone] = useState("casual");
  const [busyKey, setBusyKey] = useState("");

  const copyText = async (text, key) => {
    if (!text) return;
    setBusyKey(key);
    try {
      await navigator.clipboard.writeText(text);
      toast.success(key === "link" ? "Link copied" : "Message copied");
    } catch {
      toast.error("Couldn't copy — try selecting manually.");
    } finally {
      setTimeout(() => setBusyKey(""), 400);
    }
  };

  const message = (tile.templates && tile.templates[tone]) || "";

  return (
    <div
      className="bg-white border border-linen-border rounded-3xl shadow-soft p-5 flex flex-col"
      data-testid={`outreach-tile-${tile.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 w-8 h-8 rounded-full bg-teal/10 flex items-center justify-center shrink-0">
          <Users size={15} className="text-teal" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-heading font-medium text-lg text-navy leading-tight">{tile.title}</h3>
          <p className="text-[11px] uppercase tracking-widest text-ink-500 mt-0.5">{tile.audience}</p>
          <p className="mt-2 text-sm text-ink-600 leading-relaxed">{tile.description}</p>
          {tile.extra_note && (
            <p className="mt-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
              {tile.extra_note}
            </p>
          )}
        </div>
      </div>

      {/* Link row */}
      <div className="mt-4 flex items-center gap-2">
        <div className="flex-1 min-w-0 rounded-xl bg-linen/70 border border-linen-border px-3 py-2 flex items-center gap-2">
          <LinkIcon size={13} className="text-ink-500 shrink-0" />
          <input
            readOnly
            value={tile.url}
            data-testid={`outreach-tile-${tile.id}-url`}
            className="flex-1 min-w-0 bg-transparent text-xs text-navy font-mono truncate outline-none"
          />
        </div>
        <button
          type="button"
          onClick={() => copyText(tile.url, "link")}
          disabled={busyKey === "link"}
          data-testid={`outreach-tile-${tile.id}-copy-link`}
          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
        >
          <Copy size={13} /> Copy link
        </button>
        <a
          href={tile.url}
          target="_blank"
          rel="noreferrer"
          data-testid={`outreach-tile-${tile.id}-open`}
          title="Preview"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white border border-linen-border text-navy hover:border-teal"
        >
          <ExternalLink size={14} />
        </a>
      </div>

      {/* Tone selector + message */}
      {message && (
        <div className="mt-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-ink-500">
              <MessageSquare size={12} /> Ready-to-send message
            </div>
            <div className="inline-flex bg-linen/70 rounded-full p-0.5 border border-linen-border">
              {["casual", "formal", "followup"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTone(t)}
                  data-testid={`outreach-tile-${tile.id}-tone-${t}`}
                  className={`text-[11px] px-2.5 py-1 rounded-full transition-colors ${
                    tone === t ? "bg-white text-navy shadow-sm" : "text-ink-500 hover:text-navy"
                  }`}
                >
                  {t === "followup" ? "Follow-up" : t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <textarea
            value={message}
            readOnly
            rows={4}
            data-testid={`outreach-tile-${tile.id}-message`}
            className="w-full text-xs text-navy bg-linen/40 border border-linen-border rounded-xl p-3 leading-relaxed resize-none"
          />
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => copyText(message, "message")}
              disabled={busyKey === "message"}
              data-testid={`outreach-tile-${tile.id}-copy-message`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
            >
              <Copy size={13} /> Copy message
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
